import { App } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiChatDocumentMessagePayloadRoleEnum } from '../../../enums/AiChatDocumentMessagePayloadRoleEnum';
import type { AiChatDocumentMessagePayload } from '../../../interfaces/AiChatDocumentMessagePayload';
import type { WebSocketMessage } from '../../../interfaces/WebSocketMessage';
import {
  applyAiChatStreamPacket,
  extractAiChatStreamPacket,
  type AiChatStreamPacket,
} from '../../../lib/chat/applyAiChatStreamPacket';
import { FileService } from '../../../services/fileApiService';
import { useAppStore } from '../../../store/store';
import { dateToTicks } from '../../../utils/utils';
import type { ChatRealtimePhase } from '../../../utils/webSocketConnectionStatus';
import {
  cancelPendingAssistant,
  newDocumentChatClientKey,
} from '../../../components/Ai/documentChatActions';

/** Hard ceiling while waiting for an AI reply (stream / pending). */
export const AI_RESPONSE_MAX_WAIT_MS = 10 * 60 * 1000;

/** Brief delay before one FE auto-retry on GPU_BUSY. */
export const GPU_BUSY_RETRY_DELAY_MS = 1500;

/** HTTP POST race timeout (pre-stream ack). */
export const HTTP_REQUEST_TIMEOUT_MS = 120_000;

const fileService = new FileService();

type ChatWithDocumentResponse = {
  guidChat?: string;
  messageId?: number;
  message_id?: number;
};

export function mapHistoryMessage(msg: {
  id?: number;
  role: string;
  content: string;
  createdAt: number;
  guidChat: string;
}): AiChatDocumentMessagePayload {
  const roleLower = msg.role.toLowerCase();
  let role: AiChatDocumentMessagePayloadRoleEnum;
  if (roleLower === 'user') {
    role = AiChatDocumentMessagePayloadRoleEnum.User;
  } else if (roleLower === 'assistant') {
    role = AiChatDocumentMessagePayloadRoleEnum.Assistant;
  } else {
    role = AiChatDocumentMessagePayloadRoleEnum.System;
  }
  return {
    id: msg.id,
    messageId: msg.id,
    clientKey: msg.id != null ? `hist-${msg.id}` : `hist-${msg.createdAt}-${msg.role}`,
    role,
    content: msg.content,
    createdAt: msg.createdAt,
    guidChat: msg.guidChat,
    status: 'Done',
  };
}

type Params = {
  fileId: number | string;
  isRealtimeConnected: boolean;
  realtimePhase: ChatRealtimePhase;
  forceStickToBottom: () => void;
  setInputClamped: (next: string) => void;
  stopDictation: () => void;
};

/**
 * Document-scoped chat messaging: send / cancel / regenerate / stream /
 * response timeout / one-shot GPU_BUSY retry / reconnect history refetch.
 */
export function useDocumentChatMessaging({
  fileId,
  isRealtimeConnected,
  realtimePhase,
  forceStickToBottom,
  setInputClamped,
  stopDictation,
}: Params) {
  const { notification, message: toast } = App.useApp();
  const { t } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [chatHistory, setChatHistory] = useState<AiChatDocumentMessagePayload[]>([]);
  const [guidChat, setGuidChat] = useState<string | null>(null);

  const guidChatRef = useRef<string | null>(null);
  const websocketTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const streamQueueRef = useRef<AiChatStreamPacket[]>([]);
  const streamRafRef = useRef<number | null>(null);
  const finalizingRef = useRef(false);
  /** Bump on send/retry/regenerate and on cancel so late POST/WS finalize is ignored. */
  const requestEpochRef = useRef(0);
  const livePendingRef = useRef(false);
  const gpuBusyAutoRetriedRef = useRef(false);
  const userCancelledRef = useRef(false);
  const prevRealtimePhaseRef = useRef<ChatRealtimePhase | null>(null);

  useEffect(() => {
    guidChatRef.current = guidChat;
  }, [guidChat]);

  const clearWebsocketTimeout = useCallback(() => {
    if (websocketTimeoutRef.current) {
      clearTimeout(websocketTimeoutRef.current);
      websocketTimeoutRef.current = null;
    }
  }, []);

  const clearStreamQueue = useCallback(() => {
    if (streamRafRef.current != null) {
      cancelAnimationFrame(streamRafRef.current);
      streamRafRef.current = null;
    }
    streamQueueRef.current = [];
  }, []);

  /** Replace last Pending assistant with authoritative Done content. No Pending → no-op (cancel). */
  const finalizeAssistantMessage = useCallback(
    (content: string, messageId?: number) => {
      setChatHistory((prev) => {
        const next = [...prev];
        for (let i = next.length - 1; i >= 0; i -= 1) {
          const msg = next[i];
          if (
            msg &&
            msg.role === AiChatDocumentMessagePayloadRoleEnum.Assistant &&
            msg.status === 'Pending' &&
            !msg.isError
          ) {
            next[i] = {
              ...msg,
              content,
              status: 'Done',
              id: messageId ?? msg.id,
              messageId: messageId ?? msg.messageId,
              streamSeq: undefined,
              streamActivities: msg.streamActivities?.map((activity) =>
                activity.phase === 'done' ? activity : { ...activity, phase: 'done' as const },
              ),
              streamActivity: msg.streamActivity
                ? msg.streamActivity.phase === 'done'
                  ? msg.streamActivity
                  : { ...msg.streamActivity, phase: 'done' as const }
                : undefined,
            };
            return next;
          }
        }
        return prev;
      });
      livePendingRef.current = false;
      setIsLoading(false);
      clearWebsocketTimeout();
      clearStreamQueue();
    },
    [clearStreamQueue, clearWebsocketTimeout],
  );

  const addErrorMessage = useCallback(
    (originalPrompt: string, errorRaw?: string) => {
      const content = errorRaw && errorRaw.trim().length > 0 ? errorRaw : 'chat.genericError';
      setChatHistory((prev) => {
        const next = [...prev];
        for (let i = next.length - 1; i >= 0; i -= 1) {
          const msg = next[i];
          if (
            msg &&
            msg.role === AiChatDocumentMessagePayloadRoleEnum.Assistant &&
            msg.status === 'Pending' &&
            !msg.isError
          ) {
            next[i] = {
              ...msg,
              content,
              status: 'Error',
              isError: true,
              originalPrompt,
              streamSeq: undefined,
              streamActivities: undefined,
              streamActivity: undefined,
            };
            return next;
          }
        }
        next.push({
          role: AiChatDocumentMessagePayloadRoleEnum.Assistant,
          content,
          createdAt: dateToTicks(new Date()),
          guidChat: guidChatRef.current || '',
          isError: true,
          status: 'Error',
          originalPrompt,
        });
        return next;
      });
      livePendingRef.current = false;
      setIsLoading(false);
      clearWebsocketTimeout();
      clearStreamQueue();
      if (content !== 'GPU_BUSY') {
        notification.error({
          title: tRef.current('chat.genericError'),
          duration: 4,
        });
      }
    },
    [clearStreamQueue, clearWebsocketTimeout, notification],
  );

  const armResponseTimeout = useCallback(
    (epoch: number, originalPrompt: string) => {
      clearWebsocketTimeout();
      websocketTimeoutRef.current = setTimeout(() => {
        if (epoch !== requestEpochRef.current) return;
        addErrorMessage(originalPrompt, 'chat.responseTimeout');
      }, AI_RESPONSE_MAX_WAIT_MS);
    },
    [addErrorMessage, clearWebsocketTimeout],
  );

  /** Composer stop: clear Pending UI + abort local wait. No BE cancel for document chat. */
  const handleCancelPending = useCallback(() => {
    requestEpochRef.current += 1;
    livePendingRef.current = false;
    userCancelledRef.current = true;
    clearWebsocketTimeout();
    clearStreamQueue();
    finalizingRef.current = false;
    stopDictation();
    setIsLoading(false);
    setChatHistory((prev) => cancelPendingAssistant(prev));
  }, [clearStreamQueue, clearWebsocketTimeout, stopDictation]);

  const callAiWithTimeout = useCallback(
    async (prompt: string, currentGuidChat?: string | null) => {
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout')), HTTP_REQUEST_TIMEOUT_MS);
      });
      const effectiveGuidChat = currentGuidChat || guidChatRef.current;
      const params = effectiveGuidChat
        ? { fileId, prompt, guidChat: effectiveGuidChat }
        : { fileId, prompt };
      const apiPromise = fileService.aiDocumentChat(params);
      return Promise.race([apiPromise, timeoutPromise]) as Promise<
        string | ChatWithDocumentResponse
      >;
    },
    [fileId],
  );

  const patchPendingMessageIds = useCallback((res: ChatWithDocumentResponse) => {
    const messageId = Number(res.messageId ?? res.message_id);
    const nextGuid = res.guidChat;
    if (nextGuid) {
      setGuidChat(nextGuid);
      guidChatRef.current = nextGuid;
    }
    if (!Number.isFinite(messageId) && !nextGuid) return;

    setChatHistory((prev) => {
      const next = [...prev];
      for (let i = next.length - 1; i >= 0; i -= 1) {
        const msg = next[i];
        if (
          msg &&
          msg.role === AiChatDocumentMessagePayloadRoleEnum.Assistant &&
          msg.status === 'Pending' &&
          !msg.isError
        ) {
          next[i] = {
            ...msg,
            guidChat: nextGuid || msg.guidChat,
            ...(Number.isFinite(messageId) ? { messageId, id: msg.id ?? messageId } : {}),
          };
          break;
        }
      }
      for (let i = next.length - 1; i >= 0; i -= 1) {
        const userMsg = next[i];
        if (userMsg?.role === AiChatDocumentMessagePayloadRoleEnum.User && nextGuid) {
          next[i] = { ...userMsg, guidChat: nextGuid };
          break;
        }
      }
      return next;
    });
  }, []);

  const loadExistingChat = useCallback(async () => {
    setIsLoadingHistory(true);
    try {
      const response = await fileService.getAiDocumentChat(fileId);
      if (Array.isArray(response) && response.length > 0) {
        const lastMessage = response[response.length - 1];
        if (lastMessage.guidChat) {
          setGuidChat(lastMessage.guidChat);
          guidChatRef.current = lastMessage.guidChat;
        }
        setChatHistory(response.map(mapHistoryMessage));
      } else {
        setChatHistory([]);
      }
    } catch {
      // chat stays empty / previous
    } finally {
      setIsLoadingHistory(false);
    }
  }, [fileId]);

  const handleRetry = useCallback(
    async (errorMessageIndex: number, options?: { fromAutoRetry?: boolean }) => {
      const errorMessage = chatHistory[errorMessageIndex];
      if (!errorMessage?.originalPrompt || isLoading) return;
      if (!options?.fromAutoRetry) {
        gpuBusyAutoRetriedRef.current = false;
      }

      const originalPrompt = errorMessage.originalPrompt;
      const historyBeforeRetry = chatHistory.filter((_, idx) => idx !== errorMessageIndex);
      const userMessage: AiChatDocumentMessagePayload = {
        role: AiChatDocumentMessagePayloadRoleEnum.User,
        content: originalPrompt,
        createdAt: dateToTicks(new Date()),
        guidChat: guidChat || '',
        status: 'Done',
        clientKey: newDocumentChatClientKey(),
      };
      const pendingAssistant: AiChatDocumentMessagePayload = {
        role: AiChatDocumentMessagePayloadRoleEnum.Assistant,
        content: '',
        createdAt: dateToTicks(new Date()),
        guidChat: guidChat || '',
        status: 'Pending',
        clientKey: newDocumentChatClientKey(),
      };

      const epoch = ++requestEpochRef.current;
      livePendingRef.current = true;
      userCancelledRef.current = false;
      forceStickToBottom();
      setChatHistory([...historyBeforeRetry, userMessage, pendingAssistant]);
      setIsLoading(true);

      try {
        const res = await callAiWithTimeout(originalPrompt);
        if (epoch !== requestEpochRef.current) return;
        if (typeof res === 'string') {
          finalizeAssistantMessage(res);
        } else if (res && typeof res === 'object' && res.guidChat) {
          patchPendingMessageIds(res);
          armResponseTimeout(epoch, originalPrompt);
        }
      } catch {
        if (epoch !== requestEpochRef.current) return;
        setChatHistory(historyBeforeRetry);
        setInputClamped(originalPrompt);
        addErrorMessage(originalPrompt);
      }
    },
    [
      addErrorMessage,
      armResponseTimeout,
      callAiWithTimeout,
      chatHistory,
      finalizeAssistantMessage,
      forceStickToBottom,
      guidChat,
      isLoading,
      patchPendingMessageIds,
      setInputClamped,
    ],
  );

  /** Regenerate last assistant reply without duplicating the user bubble. */
  const handleRegenerate = useCallback(
    async (assistantIndex: number, options?: { fromAutoRetry?: boolean }) => {
      if (isLoading) return;
      if (!options?.fromAutoRetry) {
        gpuBusyAutoRetriedRef.current = false;
      }

      let userPrompt: string | undefined;
      for (let i = assistantIndex - 1; i >= 0; i -= 1) {
        if (chatHistory[i]?.role === AiChatDocumentMessagePayloadRoleEnum.User) {
          userPrompt = chatHistory[i]?.content;
          break;
        }
      }
      if (!userPrompt?.trim()) return;

      const historyWithoutAssistant = chatHistory.filter((_, idx) => idx !== assistantIndex);
      const pendingAssistant: AiChatDocumentMessagePayload = {
        role: AiChatDocumentMessagePayloadRoleEnum.Assistant,
        content: '',
        createdAt: dateToTicks(new Date()),
        guidChat: guidChat || '',
        status: 'Pending',
        clientKey: newDocumentChatClientKey(),
      };

      const epoch = ++requestEpochRef.current;
      livePendingRef.current = true;
      userCancelledRef.current = false;
      forceStickToBottom();
      setChatHistory([...historyWithoutAssistant, pendingAssistant]);
      setIsLoading(true);

      try {
        const res = await callAiWithTimeout(userPrompt);
        if (epoch !== requestEpochRef.current) return;
        if (typeof res === 'string') {
          finalizeAssistantMessage(res);
        } else if (res && typeof res === 'object' && res.guidChat) {
          patchPendingMessageIds(res);
          armResponseTimeout(epoch, userPrompt);
        }
      } catch {
        if (epoch !== requestEpochRef.current) return;
        addErrorMessage(userPrompt);
      }
    },
    [
      addErrorMessage,
      armResponseTimeout,
      callAiWithTimeout,
      chatHistory,
      finalizeAssistantMessage,
      forceStickToBottom,
      guidChat,
      isLoading,
      patchPendingMessageIds,
    ],
  );

  const handleSend = useCallback(
    async (messageToSend: string) => {
      const trimmed = messageToSend.trim();
      if (!trimmed || isLoading || !isRealtimeConnected) return;

      const originalPrompt = trimmed;
      stopDictation();

      const userMessage: AiChatDocumentMessagePayload = {
        role: AiChatDocumentMessagePayloadRoleEnum.User,
        content: originalPrompt,
        createdAt: dateToTicks(new Date()),
        guidChat: guidChat || '',
        status: 'Done',
        clientKey: newDocumentChatClientKey(),
      };
      const pendingAssistant: AiChatDocumentMessagePayload = {
        role: AiChatDocumentMessagePayloadRoleEnum.Assistant,
        content: '',
        createdAt: dateToTicks(new Date()),
        guidChat: guidChat || '',
        status: 'Pending',
        clientKey: newDocumentChatClientKey(),
      };

      const epoch = ++requestEpochRef.current;
      livePendingRef.current = true;
      userCancelledRef.current = false;
      gpuBusyAutoRetriedRef.current = false;
      forceStickToBottom();
      setChatHistory((prev) => [...prev, userMessage, pendingAssistant]);
      setIsLoading(true);

      try {
        const res = await callAiWithTimeout(originalPrompt);
        if (epoch !== requestEpochRef.current) return;

        if (typeof res === 'string') {
          finalizeAssistantMessage(res);
        } else if (res && typeof res === 'object' && res.guidChat) {
          patchPendingMessageIds(res);
          armResponseTimeout(epoch, originalPrompt);
        }
      } catch {
        if (epoch !== requestEpochRef.current) return;
        setInputClamped(originalPrompt);
        addErrorMessage(originalPrompt);
      }
    },
    [
      addErrorMessage,
      armResponseTimeout,
      callAiWithTimeout,
      finalizeAssistantMessage,
      forceStickToBottom,
      guidChat,
      isLoading,
      isRealtimeConnected,
      patchPendingMessageIds,
      setInputClamped,
      stopDictation,
    ],
  );

  /** One auto-retry when last assistant surfaces GPU_BUSY. */
  useEffect(() => {
    const last = chatHistory[chatHistory.length - 1];
    if (!last || last.role !== AiChatDocumentMessagePayloadRoleEnum.Assistant) return;
    if (!(last.isError || last.status === 'Error')) return;
    if (last.content !== 'GPU_BUSY') return;
    if (gpuBusyAutoRetriedRef.current || userCancelledRef.current || isLoading) return;

    gpuBusyAutoRetriedRef.current = true;
    toastRef.current.info(tRef.current('chat.gpuBusyRetrying'));
    const idx = chatHistory.length - 1;
    window.setTimeout(() => {
      if (userCancelledRef.current) return;
      void handleRetry(idx, { fromAutoRetry: true });
    }, GPU_BUSY_RETRY_DELAY_MS);
  }, [chatHistory, handleRetry, isLoading]);

  // Initial history load + cleanup on file change
  useEffect(() => {
    void loadExistingChat();
    return () => {
      clearWebsocketTimeout();
      clearStreamQueue();
    };
  }, [clearStreamQueue, clearWebsocketTimeout, loadExistingChat]);

  // Refetch history on hub reconnect edge into connected
  useEffect(() => {
    const prev = prevRealtimePhaseRef.current;
    prevRealtimePhaseRef.current = realtimePhase;
    if (realtimePhase !== 'connected') return;
    if (prev === null || prev === 'connected') return;
    void loadExistingChat();
  }, [loadExistingChat, realtimePhase]);

  // Stream + finalize via EventHub ReceiveEvent → events.webSocketEvent
  useEffect(() => {
    const flushStreamQueue = () => {
      streamRafRef.current = null;
      const batch = streamQueueRef.current;
      if (batch.length === 0) return;
      streamQueueRef.current = [];
      setChatHistory((previous) => {
        let next = previous;
        for (const packet of batch) {
          next = applyAiChatStreamPacket(next, packet);
        }
        return next;
      });
    };

    const handleFinalize = async (d: { event_type?: string; file_id?: number; guid?: string }) => {
      const eventType = d?.event_type;
      if (eventType !== 'document_chat' && eventType !== 'chat_response') return;
      if (d?.file_id != null && Number(d.file_id) !== Number(fileId)) return;
      if (!livePendingRef.current) return;

      const activeGuid = guidChatRef.current;
      if (d?.guid != null && activeGuid != null && d.guid !== activeGuid) return;
      if (!activeGuid && !d?.guid) return;

      const guidForGet = d?.guid ?? activeGuid;
      if (!guidForGet || finalizingRef.current) return;

      finalizingRef.current = true;
      try {
        const response = await fileService.getAiDocumentChat(fileId, guidForGet);
        if (!livePendingRef.current) return;
        if (Array.isArray(response) && response.length > 0) {
          const lastMessage = response[response.length - 1];
          if (lastMessage?.guidChat) {
            setGuidChat(lastMessage.guidChat);
            guidChatRef.current = lastMessage.guidChat;
          }
          const status = String(
            (lastMessage as { status?: string; Status?: string }).status ??
              (lastMessage as { Status?: string }).Status ??
              '',
          ).toLowerCase();
          const errorRaw =
            (lastMessage as { error?: string; Error?: string; errorMessage?: string }).error ??
            (lastMessage as { Error?: string }).Error ??
            (lastMessage as { errorMessage?: string }).errorMessage;
          if (status === 'error' || errorRaw) {
            const prompt =
              [...response].reverse().find((m) => String(m.role).toLowerCase() === 'user')
                ?.content ?? '';
            addErrorMessage(prompt, errorRaw || 'chat.genericError');
            return;
          }
          finalizeAssistantMessage(lastMessage.content, lastMessage.id);
        }
      } catch {
        if (!livePendingRef.current) return;
        addErrorMessage('WebSocket response failed');
      } finally {
        finalizingRef.current = false;
      }
    };

    const handleWebSocketEvent = (event: WebSocketMessage) => {
      const streamPacket = extractAiChatStreamPacket(
        event as WebSocketMessage & Record<string, unknown>,
      );
      if (streamPacket) {
        const conversationId =
          streamPacket.conversation_id ?? streamPacket.conversationId ?? undefined;
        const activeGuid = guidChatRef.current;
        if (conversationId && activeGuid && conversationId !== activeGuid) {
          return;
        }
        streamQueueRef.current.push(streamPacket);
        if (streamRafRef.current == null) {
          streamRafRef.current = requestAnimationFrame(flushStreamQueue);
        }
        return;
      }

      const d = event?.data as
        { event_type?: string; file_id?: number; guid?: string; type?: string } | undefined;
      if (event?.type === 'ai_event' || d?.event_type) {
        void handleFinalize(d ?? {});
      }
    };

    const unsubscribeWebSocketEvent = useAppStore
      .getState()
      .events.on.webSocketEvent(handleWebSocketEvent);

    return () => {
      unsubscribeWebSocketEvent();
      clearStreamQueue();
    };
  }, [addErrorMessage, clearStreamQueue, fileId, finalizeAssistantMessage]);

  return {
    chatHistory,
    setChatHistory,
    isLoading,
    isLoadingHistory,
    guidChat,
    handleSend,
    handleCancelPending,
    handleRetry,
    handleRegenerate,
    loadExistingChat,
  };
}
